import './App.css';
import routes, {renderRoutes} from './Routes';
import {GlobalStyles, StyledEngineProvider} from "@mui/material";
import {SnackbarProvider} from "notistack";
import {Provider as StoreProvider} from 'react-redux';
import {ThemeProvider} from "@mui/material/styles";
import ScrollReset from "./components/ScrollReset";
import useSettings from "./hooks/useSettings";
import {BrowserRouter} from 'react-router-dom'
import {createMuiTheme} from "./theme";
import {configureStore} from './store';
import {BreakpointProvider} from 'react-socks'
import React, {Suspense, useEffect, useState} from "react";
import SlashScreen from "./components/LoadingScreen";
import FCMListener from "./components/FCMListener";

const store = configureStore();

function App() {
    const {settings} = useSettings();
    const theme = createMuiTheme({
        direction: settings.direction,
        responsiveFontSizes: settings.responsiveFontSizes,
        theme: settings.theme,
    });

    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [updated, setUpdated] = useState(false);

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then(registrations => {
                registrations.forEach(registration => {
                    if (registration.waiting) {
                        setUpdateAvailable(true);
                        console.log('[PWA] Update available: Service worker is waiting.');
                    }
                    registration.addEventListener('updatefound', () => {
                        if (registration.installing) {
                            registration.installing.addEventListener('statechange', () => {
                                if (registration.waiting) {
                                    setUpdateAvailable(true);
                                    console.log('[PWA] Update found: Service worker is waiting.');
                                }
                            });
                        }
                    });
                });
            });
        }
    }, []);

    useEffect(() => {
        let refreshing = false;
        function onControllerChange() {
            if (!refreshing) {
                refreshing = true;
                setUpdating(false);
                setUpdated(true);
                setTimeout(() => {
                    setUpdated(false);
                    setUpdateAvailable(false);
                    console.log('[PWA] Update complete: Reloading app.');
                    window.location.reload();
                }, 1000);
            }
        }
        if (updating) {
            navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
        }
        return () => {
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
            }
        };
    }, [updating]);

    const reloadPage = () => {
        setUpdating(true);
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then(registrations => {
                registrations.forEach(registration => {
                    if (registration.waiting) {
                        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                        console.log('[PWA] Sent SKIP_WAITING to service worker.');
                    }
                });
            });
        }
    };

    return (
        <>
            {updateAvailable && !updating && !updated && (
                <div style={{
                    position: 'fixed',
                    bottom: 24,
                    left: 0,
                    right: 0,
                    margin: '0 auto',
                    zIndex: 9999,
                    background: '#1976d2',
                    color: 'white',
                    padding: 16,
                    borderRadius: 8,
                    maxWidth: 400,
                    boxShadow: '0 2px 12px rgba(25,118,210,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <span style={{ fontWeight: 500 }}>A new version is available!</span>
                    <button onClick={reloadPage} style={{
                        marginLeft: 16,
                        background: 'white',
                        color: '#1976d2',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(25,118,210,0.12)'
                    }}>Update</button>
                </div>
            )}
            {updating && (
                <div style={{
                    position: 'fixed',
                    bottom: 24,
                    left: 0,
                    right: 0,
                    margin: '0 auto',
                    zIndex: 9999,
                    background: '#1976d2',
                    color: 'white',
                    padding: 16,
                    borderRadius: 8,
                    maxWidth: 400,
                    boxShadow: '0 2px 12px rgba(25,118,210,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 500
                }}>
                    <svg style={{marginRight: 12}} width="28" height="28" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="18" stroke="#fff" strokeWidth="4" opacity="0.2"/><circle cx="20" cy="20" r="18" stroke="#fff" strokeWidth="4" strokeDasharray="90 20" strokeLinecap="round"><animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="0.9s" repeatCount="indefinite" /></circle></svg>
                    Downloading update...
                </div>
            )}
            {updated && (
                <div style={{
                    position: 'fixed',
                    bottom: 24,
                    left: 0,
                    right: 0,
                    margin: '0 auto',
                    zIndex: 9999,
                    background: '#43a047',
                    color: 'white',
                    padding: 16,
                    borderRadius: 8,
                    maxWidth: 400,
                    boxShadow: '0 2px 12px rgba(67,160,71,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 500
                }}>
                    <svg style={{marginRight: 12}} width="28" height="28" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="18" stroke="#fff" strokeWidth="4" opacity="0.2"/><path d="M12 22l5 5 11-11" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Updated!
                </div>
            )}
            <div data-testid="app-root">
                <Suspense fallback={<SlashScreen/>}>
                    {/*<CheckConnection>*/}
                    <BreakpointProvider>
                        <StoreProvider store={store}>
                            <ThemeProvider theme={theme}>
                                <StyledEngineProvider injectFirst>
                                    <SnackbarProvider dense maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                                        {/* Use Router for custom history, BrowserRouter does not accept a history prop */}
                                        <BrowserRouter>
                                            {/*<AuthProvider>*/}
                                            <GlobalStyles/>
                                            <ScrollReset/>
                                            {renderRoutes(routes)}
                                            <FCMListener />
                                            {/*</AuthProvider>*/}
                                        </BrowserRouter>
                                    </SnackbarProvider>
                                </StyledEngineProvider>
                            </ThemeProvider>
                        </StoreProvider>
                    </BreakpointProvider>
                {/*</CheckConnection>*/}
                </Suspense>
            </div>
        </>
    );
}

export default App;
